import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  Card,
  Tag,
  Statistic,
  Pagination,
  Spin,
  message,
  Tabs,
  Form,
  Input,
  Select,
  Button,
  Upload,
  Row,
  Col,
  Empty,
  Alert,
  Typography,
} from 'antd';
import { DollarOutlined, UploadOutlined, ShoppingCartOutlined, BankOutlined, CopyOutlined } from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import { LoginContext } from '../../context/loginContext';
import dayjs from 'dayjs';
import { compressFile } from '../../utils/fileCompression';

const { Text } = Typography;

const AlumnoCreditos = () => {
  const { user } = useContext(LoginContext);
  const [creditos, setCreditos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [totalCreditosActivos, setTotalCreditosActivos] = useState(0);
  const [planes, setPlanes] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);
  const [formCreditos] = Form.useForm();
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [loadingCreditos, setLoadingCreditos] = useState(false);
  const [activeTab, setActiveTab] = useState('ver');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [datosCuenta, setDatosCuenta] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadCreditos(1, pagination.pageSize);
    loadPlanes();
    loadTiposPago();
    loadDatosCuenta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar datos de cuenta bancaria para transferencias
  const loadDatosCuenta = async () => {
    try {
      const response = await apiClient.get('/configuracion-bot/datos-cuenta');
      if (response.ok) {
        const data = await response.json();
        setDatosCuenta(data);
      }
    } catch (error) {
      console.error('Error al cargar datos de cuenta:', error);
    }
  };

  // Copiar texto al portapapeles
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copiado al portapapeles`);
    }).catch(() => {
      message.error('Error al copiar');
    });
  };

  const loadCreditos = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      const url = `/mi-creditos/mis-creditos?${params.toString()}`;
      const response = await apiClient.get(url);
      
      if (response.ok) {
        const result = await response.json();
        setCreditos(result.data || []);
        setPagination({
          current: result.page || 1,
          pageSize: result.pageSize || 10,
          total: result.total || 0,
        });
        setTotalCreditosActivos(result.totalActivos || 0);
      } else {
        message.error('Error al cargar los créditos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los créditos');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
    loadCreditos(newPagination.current, newPagination.pageSize);
  };

  // Cargar planes
  const loadPlanes = async () => {
    try {
      const response = await apiClient.get('/mi/planes');
      if (response.ok) {
        const data = await response.json();
        // Filtrar planes activos y excluir el plan "Crédito Gratuito"
        const planesFiltrados = data.filter(plan => {
          const esActivo = plan.pla_activo;
          const nombrePlan = (plan.pla_nombre || '').toLowerCase();
          const esGratuito = nombrePlan.includes('gratuito') || nombrePlan.includes('gratis');
          return esActivo && !esGratuito;
        });
        setPlanes(planesFiltrados);
        console.log('✅ Planes cargados:', planesFiltrados);
      } else {
        const error = await response.json();
        console.error('Error al cargar planes:', error);
        message.error('Error al cargar los planes disponibles');
      }
    } catch (error) {
      console.error('Error al cargar planes:', error);
      message.error('Error al cargar los planes disponibles');
    }
  };

  // Cargar tipos de pago
  const loadTiposPago = async () => {
    try {
      const response = await apiClient.get('/mi/tipo-pagos');
      if (response.ok) {
        const data = await response.json();
        setTiposPago(data);
        console.log('✅ Tipos de pago cargados:', data);
      } else {
        const error = await response.json();
        console.error('Error al cargar tipos de pago:', error);
        message.error('Error al cargar los tipos de pago');
      }
    } catch (error) {
      console.error('Error al cargar tipos de pago:', error);
      message.error('Error al cargar los tipos de pago');
    }
  };

  // Convertir archivo a base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Manejar carga de comprobante (usando beforeUpload como en administración)
  const handleComprobanteBeforeUpload = async (file) => {
    try {
      // Validar tipo de archivo
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        message.error('Solo se permiten archivos JPG, PNG o PDF');
        return false;
      }

      // Comprimir el archivo
      const compressedFile = await compressFile(file);
      setComprobanteFile(compressedFile);
      
      // Actualizar el campo del formulario para que la validación funcione
      formCreditos.setFieldsValue({ comprobante: compressedFile.name });
      
      // Validar el campo después de un pequeño delay
      setTimeout(() => {
        formCreditos.validateFields(['comprobante']).catch(() => {});
      }, 200);
      
      message.success('Comprobante cargado correctamente');
      return false; // Prevenir la subida automática
    } catch (error) {
      console.error('Error al procesar el archivo:', error);
      message.error(error.message || 'Error al procesar el archivo');
      setComprobanteFile(null);
      formCreditos.setFieldsValue({ comprobante: null });
      return false;
    }
  };

  // Manejar eliminación del comprobante
  const handleComprobanteRemove = () => {
    setComprobanteFile(null);
    formCreditos.setFieldsValue({ comprobante: null });
    setTimeout(() => {
      formCreditos.validateFields(['comprobante']).catch(() => {});
    }, 100);
  };

  // Guardar créditos
  const handleSaveCreditos = async () => {
    try {
      // Validar el formulario primero
      await formCreditos.validateFields();
      
      const values = formCreditos.getFieldsValue();
      let comprobanteBase64 = null;
      let tipoPagoId = null;
      const metodoPago = values.metodoPago;

      // Obtener el tipoPagoId según el método seleccionado
      const tipoPago = tiposPago.find(tp => tp.tip_nombre === metodoPago);
      if (tipoPago) {
        tipoPagoId = tipoPago.tip_id;
      }
      
      // Si es TRANSFERENCIA, validar que haya comprobante
      if (metodoPago === 'TRANSFERENCIA') {
        if (!comprobanteFile) {
          message.error('El comprobante es obligatorio para pagos por transferencia');
          return;
        }
        comprobanteBase64 = await getBase64(comprobanteFile);
      }

      setLoadingCreditos(true);

      const dataToSend = {
        planId: values.planId,
        tipoPagoId: tipoPagoId,
        comprobante: comprobanteBase64,
        metodoPago: metodoPago,
      };

      const response = await apiClient.post('/mi/registro-pagos', dataToSend);
      if (response.ok) {
        const result = await response.json();
        // Mostrar mensaje según el estado del pago
        if (result.registro.reg_estado_pago === 'PAGADO') {
          message.success(`Créditos cargados correctamente. Se asignaron ${result.registro.plan_cantidad_creditos} créditos.`);
        } else if (result.registro.reg_estado_pago === 'PENDIENTE') {
          message.info('Pago registrado correctamente. El pago está pendiente de aprobación. Los créditos se asignarán una vez que el administrador apruebe el pago.');
        } else {
          message.success(result.message || 'Pago registrado correctamente.');
        }
        formCreditos.resetFields();
        setComprobanteFile(null);
        // Recargar créditos y cambiar a la pestaña de ver
        await loadCreditos(1, pagination.pageSize);
        setActiveTab('ver');
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los créditos');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al cargar los créditos');
    } finally {
      setLoadingCreditos(false);
    }
  };

  const columns = [
    {
      title: 'Plan',
      dataIndex: 'plan_nombre',
      key: 'plan_nombre',
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Fecha Adquisición',
      dataIndex: 'cre_fecha_adquisicion',
      key: 'cre_fecha_adquisicion',
      render: (text) => dayjs(text).format('DD/MM/YYYY'),
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Fecha Vencimiento',
      dataIndex: 'cre_fecha_vencimiento',
      key: 'cre_fecha_vencimiento',
      render: (text) => dayjs(text).format('DD/MM/YYYY'),
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, record) => {
        const isVencido = dayjs(record.cre_fecha_vencimiento).isBefore(dayjs());
        if (record.cre_utilizado) {
          return <Tag color="red">Utilizado</Tag>;
        } else if (isVencido) {
          return <Tag color="orange">Vencido</Tag>;
        } else {
          return <Tag color="green">Activo</Tag>;
        }
      },
      width: isMobile ? 100 : undefined,
    },
    {
      title: 'Fecha Utilización',
      dataIndex: 'cre_fecha_utilizacion',
      key: 'cre_fecha_utilizacion',
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY HH:mm') : '-',
      width: isMobile ? 140 : undefined,
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '20px' }}>
        <h1 style={{ 
          color: '#ffffff', 
          marginBottom: isMobile ? '16px' : '24px',
          fontSize: isMobile ? '20px' : '24px',
        }}>
          <DollarOutlined style={{ marginRight: '8px' }} />
          Mis Créditos
        </h1>

        <Card
          style={{
            background: '#2d2d2d',
            borderColor: '#404040',
            marginBottom: isMobile ? '16px' : '24px',
          }}
        >
          <Statistic
            title="Créditos Activos"
            value={totalCreditosActivos}
            valueStyle={{ color: '#3f8600', fontSize: isMobile ? '20px' : '24px' }}
            suffix="créditos"
          />
        </Card>

        <Card
          style={{
            background: '#2d2d2d',
            borderColor: '#404040',
          }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'ver',
                label: (
                  <span>
                    <DollarOutlined /> Ver Créditos
                  </span>
                ),
                children: (
                  <Spin spinning={loading}>
                    {isMobile ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {creditos.length === 0 ? (
                          <Empty description="No hay créditos disponibles" />
                        ) : (
                          creditos.map(credito => {
                            const isVencido = dayjs(credito.cre_fecha_vencimiento).isBefore(dayjs());
                            let estadoColor = 'green';
                            let estadoText = 'Activo';
                            if (credito.cre_utilizado) {
                              estadoColor = 'red';
                              estadoText = 'Utilizado';
                            } else if (isVencido) {
                              estadoColor = 'orange';
                              estadoText = 'Vencido';
                            }
                            
                            return (
                              <Card
                                key={credito.cre_id}
                                style={{
                                  background: '#1a1a1a',
                                  borderColor: '#404040',
                                  borderRadius: '8px',
                                }}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#ffffff', marginBottom: '8px' }}>
                                        {credito.plan_nombre}
                                      </div>
                                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                                        Adquisición: {dayjs(credito.cre_fecha_adquisicion).format('DD/MM/YYYY')}
                                      </div>
                                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                                        Vencimiento: {dayjs(credito.cre_fecha_vencimiento).format('DD/MM/YYYY')}
                                      </div>
                                      {credito.cre_fecha_utilizacion && (
                                        <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                                          Utilizado: {dayjs(credito.cre_fecha_utilizacion).format('DD/MM/YYYY HH:mm')}
                                        </div>
                                      )}
                                    </div>
                                    <Tag color={estadoColor} style={{ marginLeft: '8px' }}>
                                      {estadoText}
                                    </Tag>
                                  </div>
                                </div>
                              </Card>
                            );
                          })
                        )}
                        {pagination.total > pagination.pageSize && (
                          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                            <Pagination
                              current={pagination.current}
                              pageSize={pagination.pageSize}
                              total={pagination.total}
                              onChange={(page, pageSize) => handleTableChange({ current: page, pageSize })}
                              showSizeChanger={false}
                              simple
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <Table
                        columns={columns}
                        dataSource={creditos}
                        loading={loading}
                        rowKey="cre_id"
                        scroll={{ x: isMobile ? 800 : 'max-content' }}
                        pagination={{
                          current: pagination.current,
                          pageSize: pagination.pageSize,
                          total: pagination.total,
                          showSizeChanger: !isMobile,
                          showTotal: (total) => `Total: ${total} créditos`,
                          pageSizeOptions: ['10', '20', '50', '100'],
                          responsive: true,
                        }}
                        onChange={handleTableChange}
                      />
                    )}
                  </Spin>
                ),
              },
              {
                key: 'info-cobro',
                label: (
                  <span>
                    <ShoppingCartOutlined /> Comprar Créditos
                  </span>
                ),
                children: (
                  <Alert
                    type="info"
                    showIcon
                    message="Cobros en Padel Zone"
                    description="La compra de planes y la acreditación de créditos se realizan en la caja de Padel Zone Indoor. Acá solo podés consultar tu saldo e historial de créditos."
                    style={{ marginTop: 8 }}
                  />
                ),
              },
            ]}
          />
        </Card>
      </div>
    </MainLayout>
  );
};

export default AlumnoCreditos;
